import { FC, memo, useCallback } from 'react';
import { useAppSelector } from '../../core/redux/hooks/useAppSelector';
import { selectGameType, selectIsSoundEnabled } from '../../modules/game/selectors';
import FragmentsButton from '../../modules/fragments/components/FragmentsButton';
import styles from './styles.module.scss';
import { useAppDispatch } from '../../core/redux/hooks/useAppDispatch';
import { toggleSound } from '../../modules/game/slice';
import MetroButton from '../../modules/metro/components/MetroButton';
import classNames from 'classnames';
import FragmentsMutedImage from '../../assets/images/fragments/Muted.svg';
import FragmentsUnutedImage from '../../assets/images/fragments/Unmuted.svg';
import FragmentsMutedSmallImage from '../../assets/images/fragments/MutedSmall.svg';
import FragmentsUnmutedSmallImage from '../../assets/images/fragments/UnmutedSmall.svg';

type TVolumeButtonProps = {
  className?: string;
};

const VolumeButton: FC<TVolumeButtonProps> = memo(({ className }) => {
  const gameType = useAppSelector(selectGameType);
  const isSoundEnabled = useAppSelector(selectIsSoundEnabled);
  const dispatch = useAppDispatch();

  const handleClick = useCallback(() => {
    dispatch(toggleSound(!isSoundEnabled));
  }, [dispatch, isSoundEnabled]);

  return (
    <>
      {gameType === 'fragments' && (
        <FragmentsButton className={classNames(styles.fragments, className)} color="yellow" onClick={handleClick}>
          <img alt="" className={styles.iconDefault} src={isSoundEnabled ? FragmentsUnutedImage : FragmentsMutedImage} />
          <img alt="" className={styles.iconSmall} src={isSoundEnabled ? FragmentsUnmutedSmallImage : FragmentsMutedSmallImage} />
        </FragmentsButton>
      )}

      {gameType === 'metro' && (
        <MetroButton className={classNames(styles.metro, className)} onClick={handleClick}>
          <div className={styles.icon}>
            {isSoundEnabled ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="43" height="31" fill="none">
                <path
                  stroke="currentColor"
                  strokeWidth="4"
                  d="M9.87 10.067c1.196.003 2.408-.56 3.351-1.09 1.024-.578 2.1-1.338 3.107-2.1.88-.666 1.753-1.368 2.524-1.99.115-.091.226-.181.336-.27.885-.71 1.559-1.241 2.012-1.54a.916.916 0 0 1 1.42.765v23.805a.916.916 0 0 1-1.42.765c-.46-.304-1.136-.836-2.018-1.545l-.349-.28c-.766-.617-1.63-1.312-2.503-1.973-1.003-.759-2.075-1.516-3.098-2.093-.948-.534-2.155-1.092-3.346-1.1H5.04c-1.39 0-2.582-1.191-2.582-2.582v-6.19c0-1.39 1.191-2.582 2.582-2.582h4.83Zm0 0h-.004l.007-2v2H9.87Z"
                />
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="4"
                  d="M30.56 11.385S33 12.605 33 15.656c0 3.05-2.44 4.271-2.44 4.271M33 4.064c4.88 2.44 7.321 6.101 7.321 11.592 0 5.491-2.44 9.152-7.321 11.592"
                />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="43" height="31" fill="none">
                <path
                  stroke="currentColor"
                  strokeWidth="4"
                  d="M9.85 10.067c1.196.003 2.409-.56 3.352-1.09 1.023-.578 2.1-1.338 3.106-2.1.881-.666 1.753-1.368 2.525-1.99l.336-.27c.884-.71 1.558-1.241 2.012-1.54a.916.916 0 0 1 1.42.765v23.805a.916.916 0 0 1-1.42.765c-.46-.304-1.136-.836-2.019-1.545l-.348-.28c-.767-.617-1.63-1.312-2.503-1.973-1.003-.759-2.076-1.516-3.099-2.093-.948-.534-2.154-1.092-3.345-1.1H5.021c-1.391 0-2.582-1.191-2.582-2.582v-6.19c0-1.39 1.19-2.582 2.582-2.582h4.83Zm0 0h-.003l.006-2v2h-.002Z"
                />
                <mask id="a" fill="#fff">
                  <path d="m35.139 12.756-4.15-4.15c-1.844-1.842-4.944 1.037-2.991 2.992l4.15 4.15-4.15 4.149c-1.72 1.845 1.035 4.816 2.992 2.992l4.149-4.15 4.15 4.15c1.843 1.782 4.883-1.034 2.994-2.992l-4.15-4.15 4.147-4.149c1.72-1.846-1.034-4.816-2.992-2.992l-4.149 4.15Z" />
                </mask>
                <path
                  fill="currentColor"
                  d="m30.99 8.606 3.535-3.535-.002-.002-3.533 3.537Zm4.149 4.15-3.536 3.535 3.536 3.536 3.536-3.536-3.536-3.535Zm4.15-4.15-3.41-3.658-.064.06-.062.063 3.535 3.535Zm2.991 2.992 3.537 3.535.062-.062.06-.064-3.659-3.409Zm-4.146 4.15-3.537-3.535-3.533 3.536 3.534 3.534 3.536-3.535Zm4.15 4.149 3.597-3.472-.03-.032-.032-.032-3.536 3.536Zm-2.996 2.992-3.535 3.535.03.03.03.03 3.475-3.595Zm-4.149-4.15 3.536-3.535-3.536-3.536-3.536 3.536 3.536 3.536Zm-4.15 4.15 3.41 3.658.064-.06.062-.063-3.535-3.535Zm-2.991-2.992-3.536-3.536-.062.063-.06.064 3.658 3.409Zm4.15-4.15 3.535 3.536 3.535-3.535-3.535-3.536-3.536 3.536Zm-4.15-4.149-3.538 3.534.002.002 3.536-3.536Zm-.544.544 4.15 4.15 7.07-7.072-4.149-4.15-7.07 7.072Zm11.22 4.15 4.15-4.15-7.071-7.071-4.15 4.15 7.072 7.07Zm.07-8.228-4.147 4.15 7.074 7.068 4.146-4.15-7.074-7.068Zm-4.146 11.22 4.15 4.148 7.07-7.07-4.149-4.15-7.07 7.071Zm8.226.07-4.15-4.15-7.07 7.071 4.149 4.15 7.07-7.072Zm-11.22-4.15-4.15 4.15 7.071 7.07 4.15-4.149-7.071-7.071Zm-.07 8.228 4.149-4.149-7.072-7.071-4.149 4.15 7.071 7.07Zm4.149-11.22-4.15-4.15-7.07 7.072 4.148 4.15 7.072-7.072Zm-4.148-4.147c.5.5.918 1.386.828 2.415-.076.88-.484 1.471-.824 1.799-.34.327-.892.665-1.673.727-.913.073-1.817-.27-2.41-.862l7.067-7.075c-3.407-3.403-7.82-2.017-9.922.009-1.102 1.06-2.034 2.624-2.2 4.536-.18 2.061.582 4.039 2.06 5.518l7.075-7.067Zm11.162 4.2c-.52.483-1.389.853-2.372.755-.85-.085-1.43-.48-1.761-.824a2.695 2.695 0 0 1-.73-1.642c-.08-.873.223-1.758.788-2.364l7.316 6.817c3.17-3.402 1.834-7.668-.175-9.751-1.05-1.089-2.578-2-4.441-2.186-1.996-.2-3.944.483-5.442 1.878l6.817 7.316Zm-4.012 11.104c-.493-.511-.886-1.39-.791-2.397.081-.865.483-1.45.825-1.78.34-.328.888-.66 1.655-.725.893-.076 1.789.246 2.39.827l-6.951 7.19c3.405 3.292 7.747 1.928 9.84-.086 1.095-1.054 2.019-2.6 2.197-4.488.19-2.03-.531-3.995-1.969-5.485l-7.196 6.944ZM27.581 19.23c.52-.484 1.39-.855 2.373-.756.85.085 1.43.48 1.762.824.33.343.658.886.728 1.64.08.873-.222 1.759-.788 2.366l-7.316-6.817c-3.171 3.403-1.831 7.668.175 9.75 1.049 1.089 2.577 2 4.44 2.187 1.997.2 3.945-.482 5.443-1.878l-6.817-7.316Z"
                  mask="url(#a)"
                />
              </svg>
            )}
          </div>
        </MetroButton>
      )}
    </>
  );
});

export default VolumeButton;
